// 台數計算
// 對應 PRD §6.2 台數計算、§8.3 自動算台。
//
// 台型體系對齊反編譯參考「至尊麻將王 HD」PointCalculator（見 mahjongking-ref skill）：
// - 沒有「基本胡 / 平胡」台：胡牌本身 0 台保底（屁胡），只有底（金錢層另計，不在此檔）。
//   原版平胡(id 20)未實作；對應的是 邊張 / 嵌張 / 單吊 / 無字無花 這些「等待型態 / 牌型」台。
// - 互斥用「ignore 機制」：高階台用 ignores 抵銷被它涵蓋的低階台（如大三元 ignore 三元牌刻）。
//
// 結算流程（caller 端）：
//   1. findHuPatterns() 列出所有合法分解
//   2. 對每個 pattern 跑 scoreHu，加總台數
//   3. 取台數最高的 pattern 當作最終分數（PRD §6.2 慣例、原版「取最高拆法」）

import type { THand } from './hand';
import {
  concealedTripletCount,
  dragonStats,
  hasHonorTiles,
  isAllHonors,
  isAllRuns,
  isAllTripletsOrKongs,
  numericSuitsOf,
  winWaitKind,
  windStats,
  WaitKind,
  type THuPattern,
} from './hu';
import type { TWind } from './types';
import { Suit } from '@core/types';
import { MeldType } from '@core/meld';

// ============================================================
// 對外型別
// ============================================================

/** 一筆台數明細（結算畫面逐項列出用）。 */
export type TScoreItem = {
  /**
   * 台型 i18n key（對應 ZH_TW.scoring.*）。
   * core 不持有顯示字串：顯示層用此 key 查 ZH_TW.scoring（解 core→UI 文字耦合）。
   */
  readonly key: string;
  /** 台數 */
  readonly points: number;
};

/** rule 內部回傳：在 TScoreItem 上多帶「要抵銷哪些 key」。 */
type TRuleResult = TScoreItem & { readonly ignores?: readonly string[] };

/**
 * 計算台數所需的全部上下文。
 *
 * - `pattern`：findHuPatterns 列出的某一組分解
 * - `hand`：完整手牌（用 melds 判 門清 / 全求人、用 flowers 判花牌）
 * - `isDealer`：贏家是否為莊家
 * - `dealerStreak`：莊家連莊次數 n（連 N 拉 N → 2n+1 台；0 = 沒連莊）
 * - `seatWind` / `prevailingWind`：自家門風 / 圈風（自風刻 / 圈風刻 / 正花判定）
 * - `wallRemaining`：胡牌當下牌牆剩餘張數（海底 / 河底判定）
 * - `isAfterKong`：胡的那張是否為「槓後補牌」（槓上開花判定；榮和時無意義）
 * - `isRobbingKong`：是否搶別家加槓推出的第四張牌
 * - `declaredTenpai`：是否已宣告聽牌
 */
export type TScoreContext = {
  readonly pattern: THuPattern;
  readonly hand: THand;
  readonly isDealer: boolean;
  readonly dealerStreak: number;
  readonly seatWind: TWind;
  readonly prevailingWind: TWind;
  readonly wallRemaining: number;
  readonly isAfterKong: boolean;
  readonly isRobbingKong: boolean;
  readonly declaredTenpai: boolean;
};

// ============================================================
// 常數
// ============================================================

// 風 ↔ wind rank 對應（風牌 rank 1=east, 2=south, 3=west, 4=north）
const WIND_TO_RANK: Record<TWind, number> = {
  east: 1,
  south: 2,
  west: 3,
  north: 4,
};

// 三元牌 rank → 番牌 i18n key（中=1 紅中、發=2 青發、白=3 白板）
const DRAGON_RANK_TO_KEY: Record<number, string> = {
  1: 'dragonZhong',
  2: 'dragonFa',
  3: 'dragonBai',
};

// 海底 / 河底：胡的是牌牆最後一張（牌牆剩 0）。台灣標準＝最後一張，非原版較寬的 ≤16。
const LAST_TILE_WALL_THRESHOLD = 0;

// ============================================================
// 結構查詢輔助
// ============================================================

/** pattern 中是否有「某風 rank」的刻子（含槓）。 */
const hasWindTriplet = (p: THuPattern, windRank: number): boolean =>
  p.sets.some(
    (s) => s.tiles[0]?.suit === 'wind' && s.tiles[0]?.rank === windRank && s.kind !== 'run'
  );

/** pattern 中所有成刻（含槓）的三元牌 rank（1 中 / 2 發 / 3 白）。 */
const dragonTripletRanks = (p: THuPattern): number[] => {
  const ranks: number[] = [];
  for (const s of p.sets) {
    if (s.tiles[0]?.suit === 'dragon' && s.kind !== 'run') {
      const r = s.tiles[0]?.rank;
      if (r !== undefined && !ranks.includes(r)) ranks.push(r);
    }
  }
  return ranks;
};

// ============================================================
// 個別台型 rules（每個回 TRuleResult | null；不適用回 null）
// ============================================================

// ---- 莊家 / 連莊 ----
// 莊家台：台灣標準只在「贏家本身是莊」時計（莊家放槍只影響賠付，不給閒家莊台）。
const ruleDealer = (ctx: TScoreContext): TRuleResult | null =>
  ctx.isDealer ? { key: 'dealer', points: 1 } : null;

// 連莊拉莊：連 N 拉 N = 2n+1 台，抵銷莊家台（莊家胡牌且 dealerStreak>0 才成立）。
const ruleLianzhuang = (ctx: TScoreContext): TRuleResult | null =>
  ctx.isDealer && ctx.dealerStreak > 0
    ? { key: 'lianzhuang', points: ctx.dealerStreak * 2 + 1, ignores: ['dealer'] }
    : null;

// ---- 門清系 ----
// 門清：副露區只能有暗槓，其他（吃/碰/明槓/加槓）都會破門清。
const isMenqing = (ctx: TScoreContext): boolean =>
  ctx.hand.melds.every((m) => m.type === MeldType.KongConcealed);

const ruleMenqing = (ctx: TScoreContext): TRuleResult | null =>
  isMenqing(ctx) ? { key: 'menqing', points: 1 } : null;

// 不求人：門清 + 自摸。
const ruleBuqiuren = (ctx: TScoreContext): TRuleResult | null =>
  isMenqing(ctx) && ctx.pattern.isSelfDraw ? { key: 'buqiuren', points: 1 } : null;

// 自摸。
const ruleSelfDraw = (ctx: TScoreContext): TRuleResult | null =>
  ctx.pattern.isSelfDraw ? { key: 'selfDraw', points: 1 } : null;

// ---- 番牌（自風刻 / 圈風刻 / 三元牌刻）----
const ruleZifengke = (ctx: TScoreContext): TRuleResult | null =>
  hasWindTriplet(ctx.pattern, WIND_TO_RANK[ctx.seatWind]) ? { key: 'zifengke', points: 1 } : null;

const ruleQuanfengke = (ctx: TScoreContext): TRuleResult | null =>
  hasWindTriplet(ctx.pattern, WIND_TO_RANK[ctx.prevailingWind])
    ? { key: 'quanfengke', points: 1 }
    : null;

// 三元牌刻（中/發/白各 1 台，逐刻一筆）。大三元 / 字一色 會 ignore 這些 key。
const scoreDragonKe = (ctx: TScoreContext): TRuleResult[] =>
  dragonTripletRanks(ctx.pattern)
    .map((r) => DRAGON_RANK_TO_KEY[r])
    .filter((k): k is string => k !== undefined)
    .map((key) => ({ key, points: 1 }));

// ---- 等待型態（邊張 / 嵌張 / 單吊）----
const ruleBianzhang = (ctx: TScoreContext): TRuleResult | null =>
  winWaitKind(ctx.pattern) === WaitKind.Edge ? { key: 'bianzhang', points: 1 } : null;

const ruleKanzhang = (ctx: TScoreContext): TRuleResult | null =>
  winWaitKind(ctx.pattern) === WaitKind.Closed ? { key: 'kanzhang', points: 1 } : null;

const ruleDandiao = (ctx: TScoreContext): TRuleResult | null =>
  winWaitKind(ctx.pattern) === WaitKind.Pair ? { key: 'dandiao', points: 1 } : null;

// 全求人：5 組全副露（無暗槓）+ 單吊將眼，2 台、抵銷單吊。
const ruleQuanqiuren = (ctx: TScoreContext): TRuleResult | null => {
  const allExposed =
    ctx.hand.melds.length === 5 && ctx.hand.melds.every((m) => m.type !== MeldType.KongConcealed);
  if (allExposed && winWaitKind(ctx.pattern) === WaitKind.Pair) {
    return { key: 'quanqiuren', points: 2, ignores: ['dandiao'] };
  }
  return null;
};

// ---- 暗刻系（三 / 四 / 五暗刻）----
const ruleSanAnke = (ctx: TScoreContext): TRuleResult | null =>
  concealedTripletCount(ctx.pattern) === 3 ? { key: 'sananke', points: 2 } : null;

const ruleSiAnke = (ctx: TScoreContext): TRuleResult | null =>
  concealedTripletCount(ctx.pattern) === 4
    ? { key: 'siananke', points: 5, ignores: ['sananke'] }
    : null;

const ruleWuAnke = (ctx: TScoreContext): TRuleResult | null =>
  concealedTripletCount(ctx.pattern) === 5
    ? { key: 'wuananke', points: 8, ignores: ['sananke', 'siananke'] }
    : null;

// 平胡：全順子 + 雀頭非役牌（三元/自風/圈風）+ 兩面聽 + 榮和（自摸破平胡）。2 台。
// 註：與邊張/嵌張/單吊互斥（皆非兩面），不需顯式 ignore。
const rulePinghu = (ctx: TScoreContext): TRuleResult | null => {
  if (ctx.pattern.isSelfDraw) return null;
  if (!isAllRuns(ctx.pattern)) return null;
  if (winWaitKind(ctx.pattern) !== WaitKind.Open) return null;
  const pair = ctx.pattern.pair[0];
  if (!pair) return null;
  if (pair.suit === Suit.Dragon) return null;
  if (pair.suit === 'wind') {
    if (
      pair.rank === WIND_TO_RANK[ctx.seatWind] ||
      pair.rank === WIND_TO_RANK[ctx.prevailingWind]
    ) {
      return null;
    }
  }
  return { key: 'pinghu', points: 2 };
};

// ---- 牌型台 ----
const rulePengpenghu = (ctx: TScoreContext): TRuleResult | null =>
  isAllTripletsOrKongs(ctx.pattern) ? { key: 'pengpenghu', points: 4 } : null;

const ruleHunyise = (ctx: TScoreContext): TRuleResult | null => {
  const suits = numericSuitsOf(ctx.pattern);
  return suits.size === 1 && hasHonorTiles(ctx.pattern) ? { key: 'hunyise', points: 4 } : null;
};

const ruleQingyise = (ctx: TScoreContext): TRuleResult | null => {
  const suits = numericSuitsOf(ctx.pattern);
  return suits.size === 1 && !hasHonorTiles(ctx.pattern) ? { key: 'qingyise', points: 8 } : null;
};

// 無字無花：沒有花牌，且整手沒有字牌（風/元）。
const ruleWuziwuhua = (ctx: TScoreContext): TRuleResult | null =>
  ctx.hand.flowers.length === 0 && !hasHonorTiles(ctx.pattern)
    ? { key: 'wuziwuhua', points: 2 }
    : null;

// ---- 三元 / 四喜 ----
const ruleXiaosanyuan = (ctx: TScoreContext): TRuleResult | null => {
  const s = dragonStats(ctx.pattern);
  return s.triplets === 2 && s.hasPair ? { key: 'xiaosanyuan', points: 4 } : null;
};

const ruleDasanyuan = (ctx: TScoreContext): TRuleResult | null => {
  const s = dragonStats(ctx.pattern);
  return s.triplets === 3
    ? { key: 'dasanyuan', points: 8, ignores: ['dragonZhong', 'dragonFa', 'dragonBai'] }
    : null;
};

const ruleXiaosixi = (ctx: TScoreContext): TRuleResult | null => {
  const s = windStats(ctx.pattern);
  return s.triplets === 3 && s.hasPair ? { key: 'xiaosixi', points: 8 } : null;
};

const ruleDasixi = (ctx: TScoreContext): TRuleResult | null => {
  const s = windStats(ctx.pattern);
  return s.triplets === 4
    ? { key: 'dasixi', points: 16, ignores: ['zifengke', 'quanfengke', 'xiaosixi'] }
    : null;
};

// 字一色：整手皆字牌（風/元）。必為碰碰胡且含風/元刻，抵銷之。
const ruleZiyise = (ctx: TScoreContext): TRuleResult | null =>
  isAllHonors(ctx.pattern)
    ? {
        key: 'ziyise',
        points: 16,
        ignores: ['zifengke', 'quanfengke', 'dragonZhong', 'dragonFa', 'dragonBai', 'pengpenghu'],
      }
    : null;

// 槓上開花：槓後補牌自摸（榮和無意義）。1 台。
const ruleGangshang = (ctx: TScoreContext): TRuleResult | null =>
  ctx.pattern.isSelfDraw && ctx.isAfterKong ? { key: 'gangshang', points: 1 } : null;

// 搶槓：別家宣告加槓時，以其推出的第四張榮和。1 台。
const ruleQianggang = (ctx: TScoreContext): TRuleResult | null =>
  !ctx.pattern.isSelfDraw && ctx.isRobbingKong ? { key: 'qianggang', points: 1 } : null;

// ---- 海底 / 河底 ----
const ruleHaidi = (ctx: TScoreContext): TRuleResult | null =>
  ctx.pattern.isSelfDraw && ctx.wallRemaining <= LAST_TILE_WALL_THRESHOLD
    ? { key: 'haidi', points: 1 }
    : null;

const ruleHedi = (ctx: TScoreContext): TRuleResult | null =>
  !ctx.pattern.isSelfDraw && ctx.wallRemaining <= LAST_TILE_WALL_THRESHOLD
    ? { key: 'hedi', points: 1 }
    : null;

// ---- 聽牌宣告 ----
const ruleTenpaiDeclared = (ctx: TScoreContext): TRuleResult | null =>
  ctx.declaredTenpai ? { key: 'tenpai', points: 1 } : null;

// ============================================================
// 花牌（專案規則：正花 1 台、花槓 2 台、八仙過海 8 台）
// ============================================================

const SEASON_FLOWER_RANKS = [1, 2, 3, 4] as const; // 春夏秋冬
const PLANT_FLOWER_RANKS = [5, 6, 7, 8] as const; // 梅蘭菊竹

/** 某組花牌是否四張齊全。 */
const hasCompleteFlowerGroup = (ranks: ReadonlySet<number>, group: readonly number[]): boolean =>
  group.every((rank) => ranks.has(rank));

/** 門風對應的兩張正花 rank：東=春梅、南=夏蘭、西=秋菊、北=冬竹。 */
const matchingFlowerRanks = (seatWind: TWind): readonly [number, number] => {
  const windRank = WIND_TO_RANK[seatWind];
  return [windRank, windRank + 4];
};

/**
 * 列出一般胡牌時的花牌台：
 * - 正花每張 1 台。
 * - 四季／四君子齊全時，該組以花槓 2 台取代組內正花。
 * - 八花齊全只算八仙過海 8 台，不再疊加其他花台。
 */
const scoreFlowers = (ctx: TScoreContext): TRuleResult[] => {
  const flowers = ctx.hand.flowers.filter((f) => f.suit === Suit.Flower);
  if (flowers.length === 0) return [];

  const ranks = new Set(flowers.map((f) => f.rank));
  const hasSeasons = hasCompleteFlowerGroup(ranks, SEASON_FLOWER_RANKS);
  const hasPlants = hasCompleteFlowerGroup(ranks, PLANT_FLOWER_RANKS);
  if (hasSeasons && hasPlants) return [{ key: 'bayian', points: 8 }];

  const out: TRuleResult[] = [];
  if (hasSeasons) out.push({ key: 'flowerKong', points: 2 });
  if (hasPlants) out.push({ key: 'flowerKong', points: 2 });

  const [seasonMatch, plantMatch] = matchingFlowerRanks(ctx.seatWind);
  let matchingCount = 0;
  if (!hasSeasons && ranks.has(seasonMatch)) matchingCount += 1;
  if (!hasPlants && ranks.has(plantMatch)) matchingCount += 1;
  if (matchingCount > 0) {
    out.push({ key: 'flower', points: matchingCount });
  }
  return out;
};

/**
 * 八仙過海不需要一般胡牌 pattern；取得第八張花時由 CRound 直接走此專用計分。
 * 視為自摸，因此固定包含自摸 1 台，並照常套用莊家／連莊。
 */
export const scoreEightFlowers = (
  isDealer: boolean,
  dealerStreak: number
): { items: TScoreItem[]; total: number } => {
  const items: TScoreItem[] = [];
  if (isDealer) {
    items.push(
      dealerStreak > 0
        ? { key: 'lianzhuang', points: dealerStreak * 2 + 1 }
        : { key: 'dealer', points: 1 }
    );
  }
  items.push({ key: 'selfDraw', points: 1 });
  items.push({ key: 'bayian', points: 8 });
  return { items, total: totalPoints(items) };
};

// ============================================================
// 主函式：對單一 pattern 計算所有適用台數
// ============================================================

/** 所有「pattern + ctx」型 rule。 */
const RULES: ReadonlyArray<(ctx: TScoreContext) => TRuleResult | null> = [
  ruleDealer,
  ruleLianzhuang,
  ruleMenqing,
  ruleBuqiuren,
  ruleSelfDraw,
  ruleZifengke,
  ruleQuanfengke,
  ruleBianzhang,
  ruleKanzhang,
  ruleDandiao,
  ruleQuanqiuren,
  rulePinghu,
  ruleSanAnke,
  ruleSiAnke,
  ruleWuAnke,
  rulePengpenghu,
  ruleHunyise,
  ruleQingyise,
  ruleWuziwuhua,
  ruleXiaosanyuan,
  ruleDasanyuan,
  ruleXiaosixi,
  ruleDasixi,
  ruleZiyise,
  ruleGangshang,
  ruleQianggang,
  ruleHaidi,
  ruleHedi,
  ruleTenpaiDeclared,
];

/**
 * 計算某一組分解的所有台數明細。
 *
 * 先收集所有適用台（含三元牌刻、花牌），再套用 ignore 機制：
 * 任何被保留台 ignore 掉的 key 都移除（高階台抵銷低階台，對齊原版）。
 */
export const scoreHu = (ctx: TScoreContext): TScoreItem[] => {
  const raw: TRuleResult[] = [];
  for (const rule of RULES) {
    const item = rule(ctx);
    if (item) raw.push(item);
  }
  raw.push(...scoreDragonKe(ctx));
  raw.push(...scoreFlowers(ctx));

  // 收集所有被 ignore 的 key
  const ignored = new Set<string>();
  for (const it of raw) for (const ig of it.ignores ?? []) ignored.add(ig);

  // 過濾被抵銷者，並去掉 ignores 欄位還原成 TScoreItem
  return raw.filter((it) => !ignored.has(it.key)).map(({ key, points }) => ({ key, points }));
};

/** 加總台數明細為總台數。 */
export const totalPoints = (items: readonly TScoreItem[]): number =>
  items.reduce((sum, x) => sum + x.points, 0);

// ============================================================
// 多 pattern 中挑最高台數
// ============================================================

/**
 * 從所有合法分解中挑出「台數最高」者作為最終結算。
 *
 * 同一手牌可能有多種拆法（順子怎麼配對、哪張當雀頭），台數可能不同；
 * 慣例上玩家會選台數最大的方式申報（原版「取最高拆法」）。
 */
export const scoreBestPattern = (
  patterns: readonly THuPattern[],
  fixedCtx: Omit<TScoreContext, 'pattern'>
): { items: TScoreItem[]; total: number; pattern: THuPattern } | null => {
  if (patterns.length === 0) return null;

  let best: { items: TScoreItem[]; total: number; pattern: THuPattern } | null = null;
  for (const pattern of patterns) {
    const items = scoreHu({ ...fixedCtx, pattern });
    const total = totalPoints(items);
    if (!best || total > best.total) {
      best = { items, total, pattern };
    }
  }
  return best;
};
